import { useMemo } from "react";
import { useGameStore } from "@/store/gameStore";
import { enumerateLegalActions } from "@/engine/LegalActions";
import type { EngineAction } from "@/engine/GameEngine";
import type { DeckCard } from "@/types/cards";
import Card from "./Card";

type PlayCardAction = Extract<EngineAction, { type: "play-card" }>;

// 濒死救援提示：人类非当前玩家时若有濒死队友 + 有补给可救 → 弹层
// 决策动作 = play-card (补给 → 濒死队友)
export default function RescuePrompt() {
  const state = useGameStore((s) => s.state);
  const humanId = useGameStore((s) => s.humanPlayerId);
  const rescueDismissed = useGameStore((s) => s.rescueDismissed);
  const setRescueDismissed = useGameStore((s) => s.setRescueDismissed);
  const dispatch = useGameStore((s) => s.dispatch);

  const rescueActions = useMemo<PlayCardAction[]>(() => {
    if (!state) return [];
    if (state.phase !== "play") return [];
    if (state.pendingDyingPlayers.length === 0) return [];
    // 仅限濒死"队友"：人类可救的只有本队濒死玩家
    const myTeam = state.players.find((p) => p.id === humanId)?.team;
    const dyingAllyIds = new Set(
      state.players
        .filter((p) => p.team === myTeam && p.isDying)
        .map((p) => p.id)
    );
    if (dyingAllyIds.size === 0) return [];
    return enumerateLegalActions(state, humanId, {
      rescueOnlyForOthers: true,
    }).filter(
      (a): a is PlayCardAction =>
        a.type === "play-card" &&
        typeof a.targetId === "string" &&
        dyingAllyIds.has(a.targetId)
    );
  }, [state, humanId]);

  if (!state) return null;
  if (rescueDismissed) return null;
  if (rescueActions.length === 0) return null;

  const names = new Map(state.players.map((p) => [p.id, p.hero.name]));
  const dyingNames = Array.from(
    new Set(rescueActions.map((a) => a.targetId as string))
  )
    .map((id) => names.get(id) ?? id)
    .join("、");

  const human = state.players.find((p) => p.id === humanId)!;
  // 候选牌：去重同 uid
  const candidateCards: DeckCard[] = Array.from(
    new Map(
      rescueActions
        .map((a) => human.hand.find((c) => c.uid === a.cardUid))
        .filter((c): c is DeckCard => Boolean(c))
        .map((c) => [c.uid, c])
    ).values()
  );

  function pickRescue(card: DeckCard) {
    // 取该牌对应的第一个救援动作（target 是濒死队友）
    const a = rescueActions.find((x) => x.cardUid === card.uid);
    if (a) dispatch(a);
  }

  function dismiss() {
    setRescueDismissed(true);
  }

  return (
    <div className="rescue-prompt" role="dialog" aria-modal="true">
      <div className="rescue-prompt__banner">
        <div className="rescue-prompt__title">濒死救援</div>
        <div className="rescue-prompt__info">
          队友 {dyingNames} 濒死，是否使用补给牌救援？
        </div>
      </div>
      <div className="rescue-prompt__cards">
        {candidateCards.length === 0 ? (
          <span className="rescue-prompt__empty">无可救援牌</span>
        ) : (
          candidateCards.map((c) => (
            <Card
              key={c.uid}
              card={c}
              compact
              highlight
              onClick={() => pickRescue(c)}
            />
          ))
        )}
      </div>
      <div className="rescue-prompt__actions">
        <button
          className="rescue-prompt__btn rescue-prompt__btn--dismiss"
          onClick={dismiss}
        >
          取消
        </button>
      </div>
    </div>
  );
}
